import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Check, X, Circle, CheckCircle2 } from 'lucide-react';
import { Note, ChecklistItem } from '../types';
import { useNoteStore } from '../stores/noteStore';
import { useSettingsStore, fontFamilyMap } from '../stores/settingsStore';

interface ChecklistEditorProps {
  note: Note;
}

const ChecklistEditor: React.FC<ChecklistEditorProps> = ({ note }) => {
  const {
    addChecklistItem,
    updateChecklistItem,
    deleteChecklistItem,
  } = useNoteStore();

  const { paperStyle, textPaddingTop, fontSize, fontFamily, lineSpacing } = useSettingsStore();

  const [newItemText, setNewItemText] = useState('');
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [editText, setEditText] = useState('');

  const handleAddItem = async () => {
    if (newItemText.trim()) {
      await addChecklistItem(note.id, newItemText.trim());
      setNewItemText('');
    }
  };

  const handleToggleComplete = async (item: ChecklistItem) => {
    await updateChecklistItem(note.id, item.id, { completed: !item.completed });
  };

  const handleStartEdit = (item: ChecklistItem) => {
    setEditingItem(item.id);
    setEditText(item.text);
  };

  const handleSaveEdit = async () => {
    if (editingItem && editText.trim()) {
      await updateChecklistItem(note.id, editingItem, { text: editText.trim() });
      setEditingItem(null);
      setEditText('');
    }
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setEditText('');
  };

  const handleDeleteItem = async (itemId: string) => {
    await deleteChecklistItem(note.id, itemId);
  };

  const completedCount = note.checklistItems?.filter(item => item.completed).length || 0;
  const totalCount = note.checklistItems?.length || 0;

  // Match the padding of regular notes
  const leftPadding = paperStyle === 'lined' ? '60px' : '16px';
  const rightPadding = '16px';
  const itemStyle = {
    fontFamily: fontFamilyMap[fontFamily],
    fontSize: `${fontSize}px`,
    lineHeight: `${lineSpacing}px`,
    paddingTop: `${textPaddingTop}px`,
  };

  return (
    <div>
      {/* Progress indicator */}
      {totalCount > 0 && (
        <div 
          className="flex items-center gap-2 text-gray-500 border-b border-gray-200/50 mb-0"
          style={{ 
            paddingLeft: leftPadding, 
            paddingRight: rightPadding,
            lineHeight: `${lineSpacing}px`,
            paddingTop: `${textPaddingTop}px`,
          }}
        >
          <span className="text-sm font-sans">
            {completedCount}/{totalCount} completed
          </span>
          <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden max-w-32">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${(completedCount / totalCount) * 100}%` }}
              className="h-full bg-green-500 rounded-full"
            />
          </div>
        </div>
      )}

      {/* Checklist Items - styled like notepad lines */}
      <AnimatePresence>
        {note.checklistItems?.map((item, index) => (
          <motion.div
            key={item.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ delay: index * 0.03 }}
            className="flex items-center gap-3 group"
            style={{ 
              paddingLeft: leftPadding, 
              paddingRight: rightPadding,
              ...itemStyle,
            }}
          >
            {/* Checkbox styled like handwritten circle */}
            <button
              onClick={() => handleToggleComplete(item)}
              className="flex-shrink-0 transition-transform active:scale-90"
            >
              {item.completed ? (
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              ) : (
                <Circle className="w-6 h-6 text-gray-400" />
              )}
            </button>

            {/* Item Text */}
            <div className="flex-1 min-w-0">
              {editingItem === item.id ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveEdit();
                      if (e.key === 'Escape') handleCancelEdit();
                    }}
                    className="flex-1 bg-white/80 px-2 py-1 rounded border border-yellow-400 outline-none"
                    style={{ fontFamily: fontFamilyMap[fontFamily], fontSize: `${fontSize}px` }}
                    autoFocus
                  />
                  <button
                    onClick={handleSaveEdit}
                    className="p-1.5 text-green-600 hover:bg-green-100 rounded-lg"
                  >
                    <Check className="w-5 h-5" />
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <span
                  onClick={() => handleStartEdit(item)}
                  className={`cursor-pointer block truncate ${
                    item.completed
                      ? 'line-through text-gray-400'
                      : 'text-gray-800'
                  }`}
                  style={{ fontFamily: fontFamilyMap[fontFamily], fontSize: `${fontSize}px` }}
                >
                  {item.text}
                </span>
              )}
            </div>

            {/* Delete Button - visible on hover/touch */}
            {editingItem !== item.id && (
              <button
                onClick={() => handleDeleteItem(item.id)}
                className="flex-shrink-0 p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </motion.div>
        ))}
      </AnimatePresence>

      {/* Add New Item - styled like notepad */}
      <div 
        className="flex items-center gap-3"
        style={{ 
          paddingLeft: leftPadding, 
          paddingRight: rightPadding,
          ...itemStyle,
        }}
      >
        <Plus className="w-6 h-6 text-gray-300 flex-shrink-0" />
        <input
          type="text"
          value={newItemText}
          onChange={(e) => setNewItemText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAddItem()}
          placeholder="Add item..."
          className="flex-1 bg-transparent outline-none text-gray-800 placeholder-gray-400"
          style={{ fontFamily: fontFamilyMap[fontFamily], fontSize: `${fontSize}px` }}
        />
        {newItemText.trim() && (
          <button
            onClick={handleAddItem}
            className="px-3 py-1 bg-yellow-400 text-gray-800 rounded-lg text-sm font-medium hover:bg-yellow-500 transition-colors font-sans"
          >
            Add
          </button>
        )}
      </div>
    </div>
  );
};

export default ChecklistEditor;
